import React from 'react';
import { Sparkles, ArrowRight, BrainCircuit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { formatINR } from '@/shared/utils/currency';

interface AiCopilotInsightCardProps {
  alertData?: {
    overdueCount?: number;
    dueTodayCount?: number;
    hotLeadsCount?: number;
    expiringQuotesCount?: number;
    sampleFeedbackCount?: number;
  };
  heroData?: {
    pipelineValue?: number;
    openDealsCount?: number;
    totalLeads?: number;
    wonMonthValue?: number;
  };
}

export const AiCopilotInsightCard: React.FC<AiCopilotInsightCardProps> = ({
  alertData,
  heroData,
}) => {
  const navigate = useNavigate();

  const promptChips = [
    'Which leads should I call today?',
    'Summarize my pipeline',
    'Plan my field visits',
    'Why is my target behind?',
  ];

  const handleChipClick = (prompt: string) => {
    navigate(`${ROUTES.aiCopilot}?prompt=${encodeURIComponent(prompt)}`);
  };

  let insightText: React.ReactNode;
  let ctaLabel: string;
  let ctaAction: () => void;

  if (alertData?.expiringQuotesCount && alertData.expiringQuotesCount > 0) {
    insightText = (
      <>
        {alertData.expiringQuotesCount} quotation{alertData.expiringQuotesCount === 1 ? '' : 's'}{' '}
        have had no response for over a week.{' '}
        <strong className="font-semibold text-primary">
          Following up today protects active commercial pipeline
        </strong>{' '}
        so they are worth chasing before they lapse.
      </>
    );
    ctaLabel = 'Review Quotes';
    ctaAction = () => navigate(ROUTES.quotations);
  } else if (alertData?.overdueCount && alertData.overdueCount > 0) {
    insightText = (
      <>
        {alertData.overdueCount} scheduled follow-up{alertData.overdueCount === 1 ? ' is' : 's are'}{' '}
        currently overdue.{' '}
        <strong className="font-semibold text-primary">
          Clearing pending touchpoints today prevents client dissatisfaction
        </strong>{' '}
        and every overdue item below is one your client is still waiting on.
      </>
    );
    ctaLabel = 'Open follow-ups';
    ctaAction = () => {
      document.getElementById('followup-center')?.scrollIntoView({ behavior: 'smooth' });
    };
  } else if (alertData?.hotLeadsCount && alertData.hotLeadsCount > 0) {
    insightText = (
      <>
        {alertData.hotLeadsCount} fresh inbound lead{alertData.hotLeadsCount === 1 ? ' has' : 's have'}{' '}
        been assigned to your desk today.{' '}
        <strong className="font-semibold text-primary">
          Calling newly assigned leads within 2 hours
        </strong>{' '}
        the sooner they are called, the sooner they can be qualified.
      </>
    );
    ctaLabel = 'Review Leads';
    ctaAction = () => navigate(ROUTES.pipeline);
  } else {
    insightText = (
      <>
        Nothing is overdue on your desk. You are working{' '}
        <strong className="font-semibold text-primary">
          {heroData?.openDealsCount ?? 0} open deal{heroData?.openDealsCount === 1 ? '' : 's'}
        </strong>
        {heroData?.pipelineValue ? <> worth {formatINR(heroData.pipelineValue)}</> : null}.
      </>
    );
    ctaLabel = 'View Pipeline';
    ctaAction = () => navigate(ROUTES.pipeline);
  }

  return (
    <div className="relative mb-5 overflow-hidden rounded-card border border-border bg-card p-4 shadow-xs">
      <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
        {/* Left: AI Recommendation */}
        <div className="flex items-start gap-3.5 max-w-3xl">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-card-sm bg-primary-container text-primary" aria-hidden>
            <BrainCircuit className="h-[18px] w-[18px]" />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-label-caps uppercase tracking-wider text-on-surface-variant">
                Next best action
              </span>
              <span className="rounded-lg bg-surface-variant px-2 py-0.5 text-data-xs text-on-surface-subtle">
                From your own desk
              </span>
            </div>

            <p className="mt-1 text-body-md leading-relaxed text-on-surface">
              {insightText}
            </p>

            {/* Prompt Chips */}
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-data-xs text-on-surface-variant">Ask Copilot</span>
              {promptChips.map((chip, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleChipClick(chip)}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-border bg-surface-variant px-2.5 py-1 text-data-xs text-on-surface-variant transition-colors hover:border-primary/40 hover:bg-primary-container hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Sparkles className="h-3 w-3 text-primary" aria-hidden />
                  <span>{chip}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: CTA Button */}
        <div className="shrink-0">
          <button
            type="button"
            onClick={ctaAction}
            className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2 text-body-sm font-medium text-on-primary transition-colors hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span>{ctaLabel}</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

